import EnvelopeIcon from "../../shared/Icons/EnvelopeIcon";
import { themeAtom } from "../../shared/atoms";
import HeaderTitle from "../../shared/components/HeaderTitle";
import Modal from "../../shared/components/ModalWindow";
import { toastError, toastSuccess } from "../../shared/components/ToastContainer";
import { Certificate } from "../models/certificates.interfaces";
import { sendCertificatesByEmail } from "../services/certificates.service";
import { useAtomValue } from "jotai";
import React, { useState } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  isOpen: boolean;
  certificates: Certificate[];
  onModalClose: (email: string) => void;
}

const SendDocumentsByEmailModal: React.FC<Props> = ({ isOpen, certificates, onModalClose }) => {
  const hasError = false;
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const theme = useAtomValue(themeAtom);

  const handleSendToEmail = async () => {
    try {
      setIsSending(true);
      await sendCertificatesByEmail(certificates, email);
      toastSuccess("Email sent successfully");
      onModalClose(email);
    } catch (error) {
      toastError("Email sending failed");
      console.error("Error sending certificates by email:", error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      className="z-[2000] flex h-[377px] w-[560px] items-center rounded-[6px] px-[26px]"
      isOpen={isOpen}
      closeModal={() => {
        onModalClose(email);
      }}
    >
      <HeaderTitle title="Share documents by email" className="mb-6 text-[18px]" />
      <p className={twMerge("mb-4 text-sm", theme.uwhcAssistant.textOutput)}>
        The selected file(s) will be sent to your email.
      </p>

      {hasError && (
        <div className="flex h-[42px] w-full items-center justify-between border border-[#F59692] bg-[#FFEAED] p-4 text-sm text-[#BF0002]">
          <p>Download failed.</p>
          <p>Try again.</p>
        </div>
      )}

      {/* Email */}
      <div className="mt-4 w-full">
        <label htmlFor="send-email-input" className={twMerge("mb-1 block text-sm", theme.uwhcAssistant.textOutput)}>
          <b>Email</b>
        </label>
        <input
          id="send-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          className={twMerge(
            "w-full rounded-[4px] border p-2 text-[14px] outline-none focus:outline-none focus:ring",
            theme.uwhcAssistant.inputBorder,
            theme.uwhcAssistant.inputBg,
            theme.uwhcAssistant.inputText,
            theme.headerComponent.tailwind.buttonFocusRing,
          )}
        />
      </div>

      <div className="mt-[40px] flex items-center gap-6">
        <div
          role="button"
          className={twMerge(
            "font-body w-full cursor-pointer rounded-[4px] border p-2 text-center text-[14px]",
            theme.uwhcAssistant.sendBorder,
            theme.uwhcAssistant.chatBg,
            theme.headerComponent.tailwind.buttonText,
            theme.headerComponent.tailwind.buttonHoverBg,
            theme.headerComponent.tailwind.buttonHoverText,
          )}
          onClick={() => {
            onModalClose(email);
          }}
        >
          Cancel
        </div>

        <button
          type="button"
          disabled={isSending}
          className={twMerge(
            "flex w-full items-center justify-center gap-2 rounded-[4px] p-2 text-center text-[14px] text-white",
            isSending ? "cursor-not-allowed opacity-70" : "cursor-pointer",
            theme.home.tailwind.greenBg,
          )}
          onClick={handleSendToEmail}
        >
          <EnvelopeIcon /> {isSending ? "Sending..." : "Send to email"}
        </button>
      </div>
    </Modal>
  );
};

export default SendDocumentsByEmailModal;
